import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { getVideosByCategory, sortVideos, type SortKey } from "@/utils/videoHelpers";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 6;

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "plays", label: "最多播放", icon: "ri-play-circle-line" },
  { key: "comments", label: "最多评论", icon: "ri-chat-3-line" },
];

const timeOptions = [
  { key: "all", label: "全部时间", days: 0 },
  { key: "month", label: "近一个月", days: 30 },
  { key: "quarter", label: "近三个月", days: 90 },
];

export default function CategoryPage() {
  const { categoryName } = useParams();
  const category = categories.find((item) => item.slug === categoryName);

  const [sort, setSort] = useState<SortKey>("latest");
  const [timeRange, setTimeRange] = useState("all");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta({
    title: category ? `${category.name}短视频-91短视频热门视频分类` : "视频分类-91短视频",
    description: category
      ? `91短视频为您提供丰富的${category.name}短视频内容，轻松观看、分享热门视频。`
      : "91短视频分类页，发现搞笑、娱乐、生活、美食等热门短视频内容。",
    keywords: category
      ? `${category.name}短视频,91短视频,${category.name},热门视频`
      : "91短视频分类,短视频分类,热门短视频",
  });

  const filtered = useMemo(() => {
    if (!category) return [];
    const list = getVideosByCategory(category.key);
    const days = timeOptions.find((item) => item.key === timeRange)?.days ?? 0;
    const timeFiltered =
      days === 0
        ? list
        : list.filter((video) => {
            const target = new Date(`${video.uploadDate}T00:00:00`).getTime();
            const now = new Date("2026-10-15T00:00:00").getTime();
            return (now - target) / 86400000 <= days;
          });
    return sortVideos(timeFiltered, sort);
  }, [category, sort, timeRange]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setSort("latest");
    setTimeRange("all");
    setPage(1);
  };

  const applySort = (key: SortKey) => {
    setSort(key);
    setPage(1);
  };

  const applyTime = (key: string) => {
    setTimeRange(key);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 text-xl font-bold text-foreground-900">没有找到这个分类</h1>
        <p className="mt-2 text-sm text-foreground-500">
          你访问的分类可能不存在，回到首页看看其他内容吧。
        </p>
        <Link
          to="/"
          className="mt-6 cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-800">{category.name}短视频</span>
      </nav>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-background-50">
            <i className={`${category.icon} text-2xl`}></i>
          </span>
          <div>
            <h1 className="text-xl font-bold text-foreground-900 md:text-2xl">
              {category.name}短视频
            </h1>
            <p className="mt-0.5 text-sm text-foreground-500">
              共 {filtered.length} 条内容 · {category.description}
            </p>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setFilterOpen((open) => !open)}
        className="mt-4 flex w-full cursor-pointer items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 lg:hidden"
      >
        <span className="flex items-center gap-2">
          <i className="ri-filter-3-line text-lg"></i>
          筛选与排序
        </span>
        <i className={`${filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg`}></i>
      </button>

      <div className="mt-5 flex flex-col gap-6 lg:flex-row">
        <aside
          className={`shrink-0 lg:block lg:w-64 ${filterOpen ? "block" : "hidden"}`}
        >
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 lg:sticky lg:top-20">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900">
                <i className="ri-equalizer-line text-base text-primary-500"></i>
                筛选与排序
              </h2>
              <button
                type="button"
                onClick={resetFilters}
                className="cursor-pointer whitespace-nowrap text-xs text-foreground-500 transition hover:text-primary-600"
              >
                重置
              </button>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground-400">
                排序方式
              </p>
              <div className="flex flex-col gap-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => applySort(option.key)}
                    className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition ${
                      sort === option.key
                        ? "bg-primary-50 font-medium text-primary-600"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <i className={`${option.icon} text-base`}></i>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 border-t border-background-200 pt-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground-400">
                发布时间
              </p>
              <div className="flex flex-col gap-1">
                {timeOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => applyTime(option.key)}
                    className={`flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition ${
                      timeRange === option.key
                        ? "bg-primary-50 font-medium text-primary-600"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <i className="ri-calendar-line text-base"></i>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          {pageItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-20 text-center">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <h3 className="mt-4 text-base font-semibold text-foreground-800">
                没有符合条件的内容
              </h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                试试放宽筛选条件，或者查看该分类下的全部视频。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-3">
                {pageItems.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}